import {useEffect} from "react"
import {handelTitle} from '../../utils'
import './About.css'
import {Link} from "@tanstack/react-router"
import Breadcrumb from "../../components/Breadcrumb"
import viri from '../../assets/img/viri.png'
import { useI18n } from '../../i18n/context'

const AboutContainer = () => {
  const { t } = useI18n()

  useEffect(() => {
    handelTitle(t('containers.about.title'))
  }, [])

  const techStackFrontend = [
    { name: 'React 18.x', desc: 'UI framework with hooks and concurrent features' },
    { name: 'TypeScript 5.x', desc: 'Type-safe JavaScript with strict mode' },
    { name: 'TanStack Router v1', desc: 'File-based, type-safe routing with nested routes' },
    { name: 'Vite 5.x', desc: 'Fast build tool with SSR support' },
    { name: 'React Query 5.x', desc: 'Data fetching and server state management' },
    { name: 'Bootstrap 5.x', desc: 'Responsive CSS framework with utility classes' },
    { name: 'WalletConnect AppKit', desc: 'Multi-chain wallet connection (v2 protocol)' },
    { name: 'Ethers.js v5/v6', desc: 'EVM blockchain interaction' },
    { name: 'Axios', desc: 'HTTP client for API calls' },
  ]

  const techStackBackend = [
    { name: 'Node.js + Express', desc: 'Web server with middleware ecosystem' },
    { name: 'Bun + Elysia', desc: 'Alternative SSR runtime (Bun-based)' },
    { name: 'Knex 2.x', desc: 'SQL query builder with migration support' },
    { name: 'MySQL 8.x / SQLite 3', desc: 'Relational database for persistence' },
    { name: 'ethers.js v6', desc: 'EVM blockchain library for reads/writes' },
    { name: 'Multer', desc: 'Multipart form data handling for uploads' },
    { name: 'Sharp / Jimp', desc: 'High-performance image processing' },
    { name: 'Nodemailer', desc: 'SMTP email notifications' },
    { name: 'IPFS HTTP Client', desc: 'Decentralized storage integration' },
    { name: 'Go (chainWatch)', desc: 'Blockchain event listener binary' },
  ]

  const featuresFrontend = [
    { title: 'Multi-Chain Support', desc: 'EVM chains (Songbird, Flare, XDC, Avalanche, BSC) with dynamic network switching' },
    { title: 'Wallet Connect', desc: 'SIWE auth, injected wallets (MetaMask), WalletConnect v2' },
    { title: 'NFT Marketplace', desc: 'Fixed price & auction listings, batch upload, collection management' },
    { title: 'Real-Time Updates', desc: 'Auction countdowns, live bidding via WebSocket (optional)' },
    { title: 'SSR/SEO', desc: 'Server-side rendering with dynamic Open Graph meta tags' },
    { title: 'Responsive Design', desc: 'Mobile-first Bootstrap layout with dark theme' },
  ]

  const featuresBackend = [
    { title: 'RESTful API', desc: '40+ endpoints for NFTs, users, auctions, metadata' },
    { title: 'Blockchain Sync', desc: 'Real-time event processing via Go chainWatch binary' },
    { title: 'Metadata Management', desc: 'IPFS fetching, caching, fallback strategies' },
    { title: 'Image Processing', desc: 'Upload, resize, optimize, CDN distribution' },
    { title: 'Multi-Chain RPC', desc: 'Configurable endpoints for 7+ EVM networks' },
    { title: 'Authentication', desc: 'Session-based + wallet signature options' },
  ]

  return (
    <>
      <main className="main-content mt-1 border-radius-lg text-green">
        <Breadcrumb text1="Home" text2={t('containers.about.title')}/>
        <div className="container-fluid py-4">

          <div className="row">
            <div className="col-12">

              {/* Page Header */}
              <div className="card mb-4 border-0 shadow-sm">
                <div className="card-body p-4 text-center">
                  <img
                    src={viri}
                    alt="Viri NFT Market"
                    style={{ height: '60px', marginBottom: '1rem' }}
                    onError={(e) => { e.target.style.display = 'none' }}
                  />
                  <h1 className="mb-2 text-green">{t('containers.about.title')}</h1>
                  <p className="text-secondary mb-0 lead" style={{ opacity: 0.8 }}>
                    {t('containers.about.description')}
                  </p>
                  <p className="text-muted small mt-2 mb-0">
                    {t('containers.about.ownedBy')}{' '}
                    <a
                      href="https://byteone.uk"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary text-decoration-none"
                    >
                      {t('containers.about.owner')}
                    </a>{' '}
                    | {t('containers.about.developedBy')}{' '}
                    <a
                      href="https://jamiemichaelprince.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary text-decoration-none"
                    >
                      {t('containers.about.developer')}
                    </a>
                  </p>
                </div>
              </div>

              {/* Tech Stack */}
              <div className="row mb-5">
                <div className="col-md-6 mb-4">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-header bg-transparent border-0 pt-4 px-4">
                      <h2 className="h5 mb-0">{t('containers.about.techStackFrontend')}</h2>
                      <p className="text-muted small mb-0">{t('containers.about.reactEcosystem')}</p>
                    </div>
                    <div className="card-body">
                      <ul className="list-unstyled">
                        {techStackFrontend.map((tech, i) => (
                          <li key={i} className="mb-3 pb-2 border-bottom">
                            <strong className="text-primary">{tech.name}</strong>
                            <p className="text-muted small mb-0">{tech.desc}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 mb-4">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-header bg-transparent border-0 pt-4 px-4">
                      <h2 className="h5 mb-0">{t('containers.about.techStackBackend')}</h2>
                      <p className="text-muted small mb-0">{t('containers.about.nodeDbLayer')}</p>
                    </div>
                    <div className="card-body">
                      <ul className="list-unstyled">
                        {techStackBackend.map((tech, i) => (
                          <li key={i} className="mb-3 pb-2 border-bottom">
                            <strong className="text-primary">{tech.name}</strong>
                            <p className="text-muted small mb-0">{tech.desc}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="row mb-5">
                <div className="col-md-6 mb-4">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-header bg-transparent border-0 pt-4 px-4">
                      <h2 className="h5 mb-0">{t('containers.about.frontendFeatures')}</h2>
                    </div>
                    <div className="card-body">
                      {featuresFrontend.map((feat, i) => (
                        <div key={i} className="mb-3 pb-3 border-bottom">
                          <h6 className="text-primary">{feat.title}</h6>
                          <p className="text-muted small mb-0">{feat.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="col-md-6 mb-4">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-header bg-transparent border-0 pt-4 px-4">
                      <h2 className="h5 mb-0">{t('containers.about.backendFeatures')}</h2>
                    </div>
                    <div className="card-body">
                      {featuresBackend.map((feat, i) => (
                        <div key={i} className="mb-3 pb-3 border-bottom">
                          <h6 className="text-primary">{feat.title}</h6>
                          <p className="text-muted small mb-0">{feat.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Development Stats */}
              <div className="card mb-5 border-0 shadow-sm">
                <div className="card-header bg-transparent border-0 pt-4 px-4 text-center">
                  <h2 className="h5 mb-1">{t('containers.about.devStats')}</h2>
                  <p className="text-muted small mb-0">{t('containers.about.devStatsDesc')}</p>
                </div>
                <div className="card-body">
                  <div className="row text-center">
                    <div className="col-md-6 mb-4">
                      <div className="display-4 text-primary mb-2">~55K</div>
                      <h6 className="text-uppercase text-muted">{t('containers.about.totalLoc')}</h6>
                      <small className="text-muted">
                        {t('containers.about.frontendLoc')}<br/>
                        {t('containers.about.backendLoc')}
                      </small>
                    </div>
                    <div className="col-md-6 mb-4">
                      <div className="display-4 text-primary mb-2">~2,700 hrs</div>
                      <h6 className="text-uppercase text-muted">{t('containers.about.estDevTime')}</h6>
                      <small className="text-muted">
                        {t('containers.about.singleDev')}<br/>
                        {t('containers.about.planningCodingTesting')}
                      </small>
                    </div>

                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="card mb-5 border-0 shadow-sm">
                <div className="card-header bg-transparent border-0 pt-4 px-4">
                  <h2 className="h5 mb-0">{t('containers.about.notes')}</h2>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="text-primary">{t('containers.about.architecture')}</h6>
                      <ul className="text-muted small">
                        <li>{t('containers.about.archItem1')}</li>
                        <li>{t('containers.about.archItem2')}</li>
                        <li>{t('containers.about.archItem3')}</li>
                        <li>{t('containers.about.archItem4')}</li>
                        <li>{t('containers.about.archItem5')}</li>
                      </ul>
                    </div>
                    <div className="col-md-6">
                      <h6 className="text-primary">{t('containers.about.keyChallenges')}</h6>
                      <ul className="text-muted small">
                        <li>{t('containers.about.challengeItem1')}</li>
                        <li>{t('containers.about.challengeItem2')}</li>
                        <li>{t('containers.about.challengeItem3')}</li>
                        <li>{t('containers.about.challengeItem4')}</li>
                        <li>{t('containers.about.challengeItem5')}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="text-center mb-6">
                <p className="text-muted">
                  {t('containers.about.interested')}{' '}
                  <Link to="/faq" className="text-primary text-decoration-none">{t('containers.about.faq')}</Link>
                  {' '}{t('containers.about.or')}{' '}
                  <Link to="/contact" className="text-primary text-decoration-none">{t('containers.about.contactUs')}</Link>.
                </p>
              </div>

            </div>
          </div>
        </div>
      </main>
    </>
  )
}

export default AboutContainer
